import React, { useState, useEffect, useRef, useCallback } from 'react';
import rosaryData from '../data/rosary.json';
import LiveCount from './LiveCount';
import './Prayer.css';

const Prayer = ({ prayer, prayerIndex, totalPrayers, onComplete, sessionStartTime, audioEnabled, onAudioToggle, remainingTime }) => {
  const [timeRemaining, setTimeRemaining] = useState(remainingTime || prayer.duration);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  const isCompletedRef = useRef(false);

  // Update the ref when onComplete changes
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Get mystery text based on day of week
  const getMysteryText = (mysteryNumber) => {
    const day = new Date().getDay();
    
    if (day === 1 || day === 6) { // Monday & Saturday - Joyful
      const joyfulMysteries = [
        'Joyful Mysteries (The Annunciation) - "In the sixth month, the angel Gabriel was sent from God to a town of Galilee called Nazareth, to a virgin betrothed to a man named Joseph, of the house of David, and the virgin\'s name was Mary." - Luke 1:26-27',
        'Joyful Mysteries (The Visitation) - "During those days Mary set out and traveled to the hill country in haste to a town of Judah, where she entered the house of Zechariah and greeted Elizabeth. When Elizabeth heard Mary\'s greeting, the infant leaped in her womb, and Elizabeth, filled with the holy Spirit, cried out in a loud voice and said, "Most blessed are you among women, and blessed is the fruit of your womb."" - Luke 1:39-42',
        'Joyful Mysteries (The Nativity) - "In those days a decree went out from Caesar Augustus that the whole world should be enrolled. This was the first enrollment, when Quirinius was governor of Syria. So all went to be enrolled, each to his own town. And Joseph too went up from Galilee from the town of Nazareth to Judea, to the city of David that is called Bethlehem, because he was of the house and family of David, to be enrolled with Mary, his betrothed, who was with child. While they were there, the time came for her to have her child, and she gave birth to her firstborn son. She wrapped him in swaddling clothes and laid him in a manger, because there was no room for them in the inn." - Luke 2:1-7',
        'Joyful Mysteries (The Presentation in the Temple) - "When eight days were completed for his circumcision, he was named Jesus, the name given him by the angel before he was conceived in the womb. When the days were completed for their purification according to the law of Moses, they took him up to Jerusalem to present him to the Lord, just as it is written in the law of the Lord, "Every male that opens the womb shall be consecrated to the Lord," and to offer the sacrifice of "a pair of turtledoves or two young pigeons," in accordance with the dictate in the law of the Lord." - Luke 2:21-24',
        'Joyful Mysteries (The Finding in the Temple) - "Each year his parents went to Jerusalem for the feast of Passover, and when he was twelve years old, they went up according to festival custom. After they had completed its days, as they were returning, the boy Jesus remained behind in Jerusalem, but his parents did not know it. Thinking that he was in the caravan, they journeyed for a day and looked for him among their relatives and acquaintances, but not finding him, they returned to Jerusalem to look for him. After three days they found him in the temple, sitting in the midst of the teachers, listening to them and asking them questions, and all who heard him were astounded at his understanding and his answers." - Luke 2:41-47'
      ];
      return joyfulMysteries[mysteryNumber - 1];
    } else if (day === 2 || day === 5) { // Tuesday & Friday - Sorrowful
      const sorrowfulMysteries = [
        'Sorrowful Mysteries (The Agony in the Garden) - "Then Jesus came with them to a place called Gethsemane, and he said to his disciples, "Sit here while I go over there and pray." He took along Peter and the two sons of Zebedee, and began to feel sorrow and distress. Then he said to them, "My soul is sorrowful even to death. Remain here and keep watch with me." He advanced a little and fell prostrate in prayer, saying, "My Father, if it is possible, let this cup pass from me; yet, not as I will, but as you will."" - Matthew 26:36-39',
        'Sorrowful Mysteries (The Scourging at the Pillar) - "Then he released Barabbas to them, but after he had Jesus scourged, he handed him over to be crucified." - Matthew 27:26',
        'Sorrowful Mysteries (The Crowning with Thorns) - "Then the soldiers of the governor took Jesus inside the praetorium and gathered the whole cohort around him. They stripped off his clothes and threw a scarlet military cloak about him. Weaving a crown out of thorns, they placed it on his head, and a reed in his right hand. And kneeling before him, they mocked him, saying, "Hail, King of the Jews!"" - Matthew 27:27-29',
        'Sorrowful Mysteries (The Carrying of the Cross) - "They pressed into service a passer-by, Simon, a Cyrenian, who was coming in from the country, the father of Alexander and Rufus, to carry his cross. They brought him to the place of Golgotha (which is translated Place of the Skull)." - Mark 15:21-22',
        'Sorrowful Mysteries (The Crucifixion and Death) - "When they came to the place called the Skull, they crucified him and the criminals there, one on his right, the other on his left. [Then Jesus said, "Father, forgive them, they know not what they do." They divided his garments by casting lots. The people stood by and watched; the rulers, meanwhile, sneered at him and said, "He saved others, let him save himself if he is the chosen one, the Messiah of God." Even the soldiers jeered at him. As they approached to offer him wine they called out, "If you are King of the Jews, save yourself." Above him there was an inscription that read, "This is the King of the Jews." Now one of the criminals hanging there reviled Jesus, saying, "Are you not the Messiah? Save yourself and us." The other, however, rebuking him, said in reply, "Have you no fear of God, for you are subject to the same condemnation? And indeed, we have been condemned justly, for the sentence we received corresponds to our crimes, but this man has done nothing criminal." Then he said, "Jesus, remember me when you come into your kingdom." He replied to him, "Amen, I say to you, today you will be with me in Paradise." "It was now about noon and darkness came over the whole land until three in the afternoon because of an eclipse of the sun. Then the veil of the temple was torn down the middle. Jesus cried out in a loud voice, "Father, into your hands I commend my spirit"; and when he had said this he breathed his last." - Luke 23:33-46'
      ];
      return sorrowfulMysteries[mysteryNumber - 1];
    } else if (day === 3 || day === 0) { // Wednesday & Sunday - Glorious
      const gloriousMysteries = [
        'Glorious Mysteries (The Resurrection) - "But at daybreak on the first day of the week they took the spices they had prepared and went to the tomb. They found the stone rolled away from the tomb; but when they entered, they did not find the body of the Lord Jesus. While they were puzzling over this, behold, two men in dazzling garments appeared to them. They were terrified and bowed their faces to the ground. They said to them, "Why do you seek the living one among the dead? He is not here, but he has been raised."" - Luke 24:1-5',
        'Glorious Mysteries (The Ascension) - "So then the Lord Jesus, after he spoke to them, was taken up into heaven and took his seat at the right hand of God." - Mark 16:19',
        'Glorious Mysteries (The Descent of the Holy Spirit) - "When the time for Pentecost was fulfilled, they were all in one place together. And suddenly there came from the sky a noise like a strong driving wind, and it filled the entire house in which they were. Then there appeared to them tongues as of fire, which parted and came to rest on each one of them. And they were all filled with the holy Spirit and began to speak in different tongues, as the Spirit enabled them to proclaim." - Acts 2:1-4',
        'Glorious Mysteries (The Assumption) - "Behold, from now on will all ages call me blessed. The Mighty One has done great things for me, and holy is his name." - Luke 1:48-49',
        'Glorious Mysteries (The Coronation of Mary) - "A great sign appeared in the sky, a woman clothed with the sun, with the moon under her feet, and on her head a crown of twelve stars." - Revelation 12:1'
      ];
      return gloriousMysteries[mysteryNumber - 1];
    } else if (day === 4) { // Thursday - Luminous
      const luminousMysteries = [
        'Luminous Mysteries (The Baptism of Christ in the Jordan) - "After Jesus was baptized, he came up from the water and behold, the heavens were opened [for him], and he saw the Spirit of God descending like a dove [and] coming upon him. And a voice came from the heavens, saying, "This is my beloved Son, with whom I am well pleased."" - Matthew 3:16-17',
        'Luminous Mysteries (The Wedding Feast at Cana) - "On the third day there was a wedding in Cana in Galilee, and the mother of Jesus was there. Jesus and his disciples were also invited to the wedding. When the wine ran short, the mother of Jesus said to him, "They have no wine." [And] Jesus said to her, "Woman, how does your concern affect me? My hour has not yet come." His mother said to the servers, "Do whatever he tells you."" - John 2:1-5',
        'Luminous Mysteries (Jesus Proclamation of the Coming of the Kingdom of God) - "This is the time of fulfillment. The kingdom of God is at hand. Repent, and believe in the gospel." - Mark 1:15',
        'Luminous Mysteries (The Transfiguration) - "After six days Jesus took Peter, James, and John his brother, and led them up a high mountain by themselves. And he was transfigured before them; his face shone like the sun and his clothes became white as light." - Matthew 17:1-2',
        'Luminous Mysteries (The Institution of the Eucharist) - "While they were eating, Jesus took bread, said the blessing, broke it, and giving it to his disciples said, "Take and eat; this is my body."" - Matthew 26:26'
      ];
      return luminousMysteries[mysteryNumber - 1];
    }
    return '';
  };

  // Initialize timer when component mounts or prayer changes
  useEffect(() => {
    // Reset completion flag for new prayer
    isCompletedRef.current = false;
    
    // Clear any existing interval
    if (intervalRef.current) {
      console.log('Clearing existing interval');
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Set initial timeRemaining
    let initialTime;
    if (remainingTime !== undefined && remainingTime !== null) {
      initialTime = remainingTime;
    } else {
      initialTime = prayer.duration;
    }
    
    setTimeRemaining(initialTime);
    
    // Start timer if we have time remaining
    if (initialTime > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            // Clear the interval
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            // Prevent multiple onComplete calls
            if (!isCompletedRef.current) {
              isCompletedRef.current = true;
              // Call onComplete after a short delay
              setTimeout(() => {
                if (onCompleteRef.current) {
                  onCompleteRef.current();
                }
              }, 100);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [prayerIndex]); // Only depend on prayerIndex

  // Audio handling
  useEffect(() => {
    // Always stop any currently playing audio first
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // Reset to beginning
      setIsPlaying(false);
    }

    // If the user joined a session mid-bead, the parent passes a `remainingTime`
    // that is shorter than the bead's full duration. Starting audio now would be
    // out of sync with everyone else (and would play from the bead's beginning),
    // so skip audio for this in-progress bead and let it begin on the next one.
    // Subsequent beads arrive with `remainingTime === null`, so audio resumes
    // normally — matching the synchronized prayer experience.
    const joinedMidPrayer =
      remainingTime !== undefined &&
      remainingTime !== null &&
      remainingTime < prayer.duration;

    // Then start new audio if enabled and available
    if (audioEnabled && prayer.audio && !joinedMidPrayer) {
      setAutoplayBlocked(false);
      // Add a small delay to prevent play/pause conflicts
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play()
            .then(() => {
              setIsPlaying(true);
              setAutoplayBlocked(false);
            })
            .catch(error => {
              if (error.name === 'NotAllowedError') {
                setAutoplayBlocked(true);
              }
              console.log('Audio play error (expected when no user gesture):', error.message);
            });
        }
      }, 50);
    }
  }, [audioEnabled, prayer.audio, prayerIndex]);

  const handleUnblockAudio = useCallback(() => {
    if (!audioEnabled || !audioRef.current || !autoplayBlocked) return;
    audioRef.current.play()
      .then(() => {
        setIsPlaying(true);
        setAutoplayBlocked(false);
      })
      .catch(() => {});
  }, [audioEnabled, autoplayBlocked]);

  const handleAudioEnd = () => {
    setIsPlaying(false);
  };

  // Calculate progress percentage
  const progress = ((prayer.duration - timeRemaining) / prayer.duration) * 100;

  // Get prayer text
  let prayerText = prayer.prayer;
  
  // Handle mystery prayers
  if (prayer.prayer === 'mystery1') {
    prayerText = getMysteryText(1);
  } else if (prayer.prayer === 'mystery2') {
    prayerText = getMysteryText(2);
  } else if (prayer.prayer === 'mystery3') {
    prayerText = getMysteryText(3);
  } else if (prayer.prayer === 'mystery4') {
    prayerText = getMysteryText(4);
  } else if (prayer.prayer === 'mystery5') {
    prayerText = getMysteryText(5);
  }

  return (
    <div
      className="prayer-container"
      onClick={autoplayBlocked && audioEnabled ? handleUnblockAudio : undefined}
    >
      <audio
        ref={audioRef}
        src={prayer.audio}
        onEnded={handleAudioEnd}
        preload="auto"
      />
      {autoplayBlocked && audioEnabled && (
        <div className="prayer-audio-unblock" aria-live="polite">
          Tap anywhere to start audio
        </div>
      )}
      <div className="prayer-header">
        <div className="prayer-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-info">
            <button 
              className="audio-toggle-button"
              onClick={(e) => {
                if (autoplayBlocked && audioEnabled) {
                  e.stopPropagation();
                  handleUnblockAudio();
                } else {
                  onAudioToggle(!audioEnabled);
                }
              }}
              title={autoplayBlocked && audioEnabled ? "Tap to start audio" : (audioEnabled ? "Mute audio" : "Unmute audio")}
            >
              {audioEnabled ? "🔊" : "🔇"}
            </button>
            <div className="progress-text">
              Prayer {prayerIndex + 1} of {totalPrayers}
            </div>
          </div>
        </div>
        
        <div className="time-display">
          {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
        </div>
      </div>

      <div className="prayer-content">
        <div className="prayer-text">
          {prayerText.split('\n').map((line, index) => (
            <p key={index} className="prayer-line">
              {line}
            </p>
          ))}
        </div>
      </div>

      
      <LiveCount />
    </div>
  );
};

export default Prayer;